/**
 * "Add jurisdiction" control for the legacy (template) triage grid.
 *
 * Backoffice needs a jurisdiction per assignment item, but a subject's
 * extracted jurisdictions may be missing or incomplete. This popover lets the
 * analyst pick a subject and add a jurisdiction (chosen from the common list or
 * typed as an ISO-2 code); the provider fans the subject's component tiles out
 * under the new jurisdiction.
 */

import { useState } from 'react';

import { Autocomplete, Button, Group, Popover, Select, Stack, Text } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';

import { JURISDICTIONS } from '@/constants';
import { useNpAssignmentsContext } from './NpAssignmentsContext';

// `JURISDICTIONS` is a Country[] ({ name, alpha2code, alpha3code }). Build the
// Autocomplete options as "Name (CODE)" so the analyst can search by either.
const JURISDICTION_OPTIONS = JURISDICTIONS.map((c) => ({
  code: c.alpha2code,
  name: c.name,
  label: `${c.name} (${c.alpha2code})`,
}));

export function AddJurisdictionControl() {
  const { templateSubjects, addJurisdiction } = useNpAssignmentsContext();
  const [opened, setOpened] = useState(false);
  const [subjectId, setSubjectId] = useState<string | null>(null);
  const [jurisdictionInput, setJurisdictionInput] = useState('');

  if (templateSubjects.length === 0) {
    return null;
  }

  const subjectOptions = templateSubjects.map((s) => ({ value: s.id, label: s.name }));

  const resolveJurisdiction = (): { code: string; name: string } | null => {
    const raw = jurisdictionInput.trim();
    if (!raw) {
      return null;
    }
    // Match "Name (CODE)", a bare code, or a name (case-insensitive).
    const byLabel = JURISDICTION_OPTIONS.find((o) => o.label === raw);
    if (byLabel) {
      return { code: byLabel.code, name: byLabel.name };
    }
    const upper = raw.toUpperCase();
    const byCode = JURISDICTION_OPTIONS.find((o) => o.code === upper);
    if (byCode) {
      return { code: byCode.code, name: byCode.name };
    }
    const byName = JURISDICTION_OPTIONS.find((o) => o.name.toLowerCase() === raw.toLowerCase());
    if (byName) {
      return { code: byName.code, name: byName.name };
    }
    // Fall back to a bare 2-letter code the analyst typed (unknown country).
    if (/^[A-Za-z]{2}$/.test(raw)) {
      return { code: upper, name: upper };
    }
    return null;
  };

  const handleAdd = () => {
    const jurisdiction = resolveJurisdiction();
    if (!subjectId || !jurisdiction) {
      return;
    }
    addJurisdiction(subjectId, jurisdiction);
    setJurisdictionInput('');
    setOpened(false);
  };

  return (
    <Popover opened={opened} onChange={setOpened} position="bottom-end" withArrow shadow="md">
      <Popover.Target>
        <Button
          variant="light"
          size="xs"
          leftSection={<IconPlus size={14} />}
          onClick={() => setOpened((o) => !o)}
        >
          Add jurisdiction
        </Button>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap="xs" w={260}>
          <Text size="sm" fw={500}>
            Add a jurisdiction to a subject
          </Text>
          {/* Both comboboxes must render inside the popover's DOM — their
              default document-level portal makes an option click register
              as an outside click, dismissing the whole popover. */}
          <Select
            label="Subject"
            placeholder="Pick a subject"
            data={subjectOptions}
            value={subjectId}
            onChange={setSubjectId}
            searchable
            comboboxProps={{ withinPortal: false }}
          />
          <Autocomplete
            label="Jurisdiction"
            placeholder="Country or ISO-2 code"
            data={JURISDICTION_OPTIONS.map((o) => o.label)}
            value={jurisdictionInput}
            onChange={setJurisdictionInput}
            comboboxProps={{ withinPortal: false }}
          />
          <Group justify="flex-end">
            <Button size="xs" onClick={handleAdd} disabled={!subjectId || !resolveJurisdiction()}>
              Add
            </Button>
          </Group>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
